import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getSupabase } from '../lib/supabase';
import { FiMail, FiPhone, FiUser, FiMessageSquare, FiSend, FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const LeadCaptureForm = ({ profileId, profileUsername }) => {
  const { walletAddress, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSubmitting(true);

    try {
      const supabase = getSupabase();
      const referrer = document.referrer || 'direct';
      const userAgent = navigator.userAgent;

      const { error } = await supabase
        .from('leads')
        .insert({
          profile_id: profileId,
          visitor_wallet: walletAddress || null,
          name: formData.name.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
          message: formData.message.trim() || null,
          source: 'profile',
          referrer: referrer !== window.location.href ? referrer : null,
          user_agent: userAgent,
        });

      if (error) {
        console.error('Error submitting lead:', error);
        toast.error('Failed to submit. Please try again.');
        return;
      }

      setSubmitted(true);
      toast.success('Thank you! Your message has been sent.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });

      // Reset submitted state after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="glass-dark p-6 rounded-xl border border-lime-500/50 bg-lime-500/10">
        <div className="flex items-center justify-center space-x-2 text-lime-400">
          <SafeIcon icon={FiCheck} className="text-xl" />
          <p className="font-medium">Thank you! We'll be in touch soon.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="glass-dark p-6 rounded-xl border border-dark-700">
      <h3 className="text-lg font-semibold text-lime-bright mb-4">
        Get in Touch
      </h3>
      <p className="text-sm text-dark-300 mb-6">
        Leave your contact info and we'll get back to you.
      </p>

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="lead-name" className="block text-sm font-medium text-dark-200 mb-2">
            Name *
          </label>
          <div className="relative">
            <SafeIcon icon={FiUser} className="absolute left-3 top-3 text-dark-400" />
            <input
              type="text"
              id="lead-name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="pl-10 w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              placeholder="Your name"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="lead-email" className="block text-sm font-medium text-dark-200 mb-2">
            Email
          </label>
          <div className="relative">
            <SafeIcon icon={FiMail} className="absolute left-3 top-3 text-dark-400" />
            <input
              type="email"
              id="lead-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="pl-10 w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              placeholder="your@email.com"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="lead-phone" className="block text-sm font-medium text-dark-200 mb-2">
            Phone
          </label>
          <div className="relative">
            <SafeIcon icon={FiPhone} className="absolute left-3 top-3 text-dark-400" />
            <input
              type="tel"
              id="lead-phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="pl-10 w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="lead-message" className="block text-sm font-medium text-dark-200 mb-2">
            Message
          </label>
          <div className="relative">
            <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 text-dark-400" />
            <textarea
              id="lead-message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="pl-10 pt-2 w-full px-3 py-2 glass rounded-lg text-white placeholder-dark-500 focus:ring-2 focus:ring-lime-500 focus:border-lime-500 border border-dark-700 resize-none"
              placeholder="How can we help you?"
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full glass-lime text-lime-400 px-6 py-3 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 hover:border-lime-400/70 shadow-glow-lime-sm hover:shadow-glow-lime disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
        >
          <SafeIcon icon={FiSend} className="mr-2" />
          {submitting ? 'Sending...' : 'Send Message'}
        </button>

        {isAuthenticated && walletAddress && (
          <p className="text-xs text-dark-400 text-center">
            Connected as {walletAddress.slice(0, 6)}...{walletAddress.slice(-4)}
          </p>
        )}
      </div>
    </form>
  );
};

export default LeadCaptureForm;

